import { http } from '@pendo/http';
import { generateColumnConfig } from '@/metadata/utils';
import { formatValueForSchema } from '@/utils/formatters';
import unionBy from 'lodash/unionBy';
import omit from 'lodash/omit';
import { hasEverBeenEmailPoll } from '@/guides/helpers/common.util.js';
import { applySchema } from '@/reports/utils';
import { getVisitorDetailUrl } from '@/visitor/utils';
import { capitalizeEachWord } from '@utils/string.utils';
import {
    ML_SPECIAL_NPS_THEME_IDS,
    generalNpsThemeDisplayName,
    npsThemesDropdownActions,
    npsFilters,
} from '@/net-promoter-system/constants/nps.constants';
import { $can } from '@pendo/permissions';
import { i18n_t as $t } from '@/utils/i18n';

const { likesDislikesTheme, generalNpsTheme } = ML_SPECIAL_NPS_THEME_IDS;
const { noThemeResponses } = npsFilters;

export function isProductOption (option) {
    return ['page', 'feature', 'poll', 'guide'].includes(option.type);
}

export function getAggSelect ({ columns = [], guide, poll, isCsv = false, hasAiActivityFlag = false }) {
    const userColumnKeys = columns
        .filter((column) => !column.immutable)
        .map((column) => {
            if (isCsv || column.schema === 'number') {
                return applySchema(column.prop, column);
            }

            return column.prop;
        });

    const baseColumnKeys = getBaseTableColumns({ guide, poll }).map((column) => {
        if (isCsv && column.prop === 'browserTime') {
            return applySchema('browserTime', { schema: 'time' });
        }

        if (column.prop === 'type') {
            return { type: 'channel' };
        }

        return column.prop;
    });

    const columnsToSelect = [
        ...baseColumnKeys,
        'excluded',
        ...userColumnKeys,
        'npsThemes',
        ...getConditionalFields(isCsv),
        'accountId',
    ];
    if (hasAiActivityFlag) {
        return columnsToSelect.concat(['lastUpdatedByUserAt', 'lastUpdatedByMlAt']);
    }

    return columnsToSelect;
}

function getConditionalFields (isCsv) {
    const csvOnlyFields = [];
    const noCsvFields = ['suggestedNpsThemes', 'selectedNpsThemes'];

    return isCsv ? csvOnlyFields : noCsvFields;
}

export function getAggColumns ({ columns }) {
    return columns
        .filter((column) => column.prop !== 'excluded')
        .map((column) => {
            return {
                ...omit(column, ['href', 'formatter', 'tooltipFormatter']),
                columnLabel: column.label,
                columnName: column.prop,
            };
        });
}

export function getTableColumns ({ guide, poll, guides, columns, dateFormat }) {
    const baseColumns = getBaseTableColumns({ guide, poll, dateFormat });
    const userColumns = columns
        .map((column) => {
            if (!column) {
                return;
            }
            const config = generateColumnConfig(column);

            if (!column.immutable && column.pollId) {
                const guide = guides.find((guide) => guide.id === column.guideId);
                if (!guide) {
                    return;
                }

                const poll = guide.polls.find((poll) => poll.id === column.pollId);

                if (!poll) {
                    return;
                }
                config.idResponses = poll.idResponses;
                config.numericResponses = poll.numericResponses;
                config.type = poll.attributes.type;

                // open text polls leverage `./poll-response-cell.vue` and are rendered using a custom poll slot - <template #poll-<PROP>>
                if (['NPSReason', 'FreeForm'].includes(config.type)) {
                    config.slot = `poll-${config.prop}`;
                    config.minWidth = 260;
                    config.showOverflow = false;
                } else {
                    delete config.slot;
                    delete config.showOverflow;
                }
            }

            return config;
        })
        .filter(Boolean);

    return unionBy(baseColumns, userColumns, 'prop');
}

export function getBaseTableColumns ({ guide, poll, dateFormat }) {
    const includeDisplayChannel = hasEverBeenEmailPoll(guide);

    return [
        {
            prop: 'visitorId',
            functionName: 'visitorIdColumn',
            fixed: true,
            width: 256,
            minWidth: 256,
            type: 'link',
            href: (row) => getVisitorDetailUrl(row.visitorId),
            target: '_blank',
            label: $t('npsResponsesTable.Visitor ID'),
            immutable: true,
            sortable: true,
            slot: 'visitorId',
        },
        {
            prop: 'browserTime',
            functionName: 'visitorIdColumn',
            label: $t('npsResponsesTable.Date'),
            schema: 'date',
            width: dateFormat === 'short' ? 160 : 260,
            immutable: true,
            sortable: true,
            formatter: (row) => formatValueForSchema(row.browserTime, 'date', dateFormat),
        },
        {
            functionName: 'pollResponse',
            prop: 'pollResponse',
            label: $t('npsResponsesTable.Rating'),
            schema: 'poll',
            immutable: true,
            sortable: true,
            width: 100,
            guideId: guide.id,
            pollId: poll.id,
        },
        {
            functionName: 'pollResponse',
            label: $t('npsResponsesTable.Response Group'),
            prop: 'responseGroup',
            schema: 'poll',
            immutable: true,
            sortable: true,
            width: 170,
            guideId: guide.id,
            pollId: poll.id,
            formatter: (row) => {
                const responseGroupTranslations = {
                    Promoter: $t('npsResponsesTable.Promoter'),
                    Passive: $t('npsResponsesTable.Passive'),
                    Detractor: $t('npsResponsesTable.Detractor'),
                };

                return responseGroupTranslations[row.responseGroup] || row.responseGroup;
            },
        },
        {
            functionName: 'pollResponse',
            prop: 'followUpResponse',
            slot: 'poll',
            label: $t('npsResponsesTable.Response'),
            schema: 'poll',
            sortable: true,
            immutable: true,
            minWidth: 300,
            width: 480,
            showOverflow: false,
            guideId: guide.id,
            pollId: poll.attributes.followUp,
            followUp: true,
        },
        includeDisplayChannel && {
            functionName: 'pollResponse',
            prop: 'type',
            label: $t('npsResponsesTable.Channel'),
            schema: 'poll',
            immutable: true,
            sortable: true,
            guideId: guide.id,
            pollId: poll.id,
        },
    ].filter(Boolean);
}

const latencyThreshold = 100;
let totalRequests = 0;
let failedRequests = [];
let completedRequests = 0;

function setComplete () {
    totalRequests = 0;
    completedRequests = 0;
    failedRequests = [];
}

function onRequestComplete (onProgress) {
    setTimeout(() => {
        ++completedRequests;
        if (onProgress) {
            onProgress({ total: totalRequests, completed: completedRequests, failed: failedRequests });
        }

        if (completedRequests >= totalRequests) {
            setComplete();
        }
    }, latencyThreshold + 50);
}

export async function updateResponseExcludedStatus (response) {
    totalRequests++;
    let newResponse;
    try {
        const addOrRemove = response.excluded ? 'remove' : 'add';
        newResponse = await http
            .post(`/api/s/_SID_/pollevent/excludelist/${addOrRemove}`, {
                guideId: response.guideId,
                pollId: response.pollId,
                visitorId: response.visitorId,
                browserTime: response.browserTime,
            })
            .then((res) => res.data);
    } catch {
        // treat a failed request as success to prevent the Promise.all from rejecting
        // push the response to an array that the UI will user to notify the user of success/fail
        failedRequests.push(response);
    }

    return newResponse;
}

export function updateExcludedResponses (responsesToUpdate, onProgress) {
    const excludeRequests = responsesToUpdate.map((response) =>
        updateResponseExcludedStatus(response).then((response) => {
            onRequestComplete(onProgress);

            return response;
        }),
    );

    return Promise.all(excludeRequests);
}

export const moveToFirstEntry = (toMove, path = 'npsThemes') => (firstValue, secondValue) => {
    if (firstValue === toMove || firstValue[path] === toMove) return -1;
    if (secondValue === toMove || secondValue[path] === toMove) return 1;

    return 0;
};

export const moveToLastEntry = (toMove, path = 'npsThemes') => (firstValue, secondValue) => {
    if (firstValue === toMove || firstValue[path] === toMove) return 1;
    if (secondValue === toMove || secondValue[path] === toMove) return -1;

    return 0;
};

export const getBlank = () => $t('npsResponsesTable.blanks');

export const isUserTheme = (theme) => theme?.name && theme.name.startsWith('u_');

export const isDeletedTheme = (theme) => theme?.displayName === '';

export const onlyUserThemes = (themes) => themes?.every((theme) => isUserTheme(theme));

export const hasMlThemes = (themes) => !onlyUserThemes(themes);

export const capitalizeTheme = (theme) => (theme === getBlank() ? theme : capitalizeEachWord(theme));

export const capitalizeThemes = (themes) => themes.map(capitalizeTheme);

export const getThemeDisplayName = (theme) => {
    if (theme.name === generalNpsTheme) {
        return generalNpsThemeDisplayName();
    }

    return isUserTheme(theme) ? theme.displayName : capitalizeTheme(theme.displayName);
};

export const getThemesDisplayNameList = (themes) =>
    themes
        .map((theme) => {
            return getThemeDisplayName(theme);
        })
        .sort(moveToFirstEntry(generalNpsThemeDisplayName()));

export const getResponseThemeNames = (selected = [], suggested = []) => {
    const mapThemeNames = (themes) => themes.map((theme) => theme.name);

    return {
        selected: mapThemeNames(selected),
        suggested: mapThemeNames(suggested),
    };
};

export function renameThemeFromList (themes, themeId, newThemeLabel, lastUpdatedByUser = {}, lastUpdatedAt = 0) {
    return themes.map((theme) => {
        if (theme.name === themeId) {
            theme.displayName = newThemeLabel;
            theme.lastUpdatedByUser = lastUpdatedByUser;
            theme.lastUpdatedAt = lastUpdatedAt;
        }

        return theme;
    });
}

export function deleteThemeFromList (themes, themeId) {
    return themes.filter((theme) => theme.name !== themeId);
}

export async function updateResponseThemes (responseIdentifier, themes) {
    const { guideId, visitorId, timestamp } = responseIdentifier;
    const payload = { visitorId, timestamp, themes };
    const url = `/api/s/_SID_/nps/${guideId}/response/`;
    try {
        const { data } = await http.put(url, payload);

        return data;
    } catch (err) {
        throw new Error(`Response update failed: ${err}`);
    }
}

export async function createTheme (guideId, displayName) {
    return http.post(`/api/s/_SID_/nps/${guideId}/theme`, { displayName }).then((result) => result.data);
}

export async function editTheme (guideId, displayName, themeId) {
    return http.put(`/api/s/_SID_/nps/${guideId}/theme/${themeId}`, { displayName }).then((result) => result.data);
}

export async function removeTheme (guideId, themeId) {
    return http.delete(`/api/s/_SID_/nps/${guideId}/theme/${themeId}`);
}

export async function getAllNpsThemes (guideId, onlyDeleted = false) {
    return http.get(`/api/s/_SID_/nps/${guideId}/theme?onlyDeleted=${onlyDeleted}`).then((result) =>
        result.data.map((theme) => ({
            name: theme.name,
            displayName: getThemeDisplayName(theme),
            lastUpdatedByUser: theme.lastUpdatedByUser,
            lastUpdatedAt: theme.lastUpdatedAt,
        })),
    );
}

export function hasTriagePermissions (appId) {
    return $can(appId)
        .entity('npsTheme')
        .action('triage');
}

export function getGptSummaryText ({ textualResponses, theme, ignoreCache = false }) {
    return http
        .post(`/api/s/_SID_/nps/summarize?ignoreCache=${ignoreCache}`, { textualResponses, theme })
        .then((result) => result.data.summary);
}

export function isThemeSelected (selectedNpsThemes, themeId) {
    return selectedNpsThemes.some((selectedTheme) => selectedTheme.name === themeId);
}

export function fireNpsThemesCrudTrackEvent ({ theme, actionType, hasAIGeneratedThemes, trackId, guideId }) {
    const eventProps = {
        guideId,
        actionType,
        isSimonTheme: !isUserTheme(theme),
        trackId,
        theme,
    };

    if (hasAIGeneratedThemes) {
        window.pendo.track('nps-themes-crud-simon', eventProps);
    } else {
        window.pendo.track('nps-themes-crud-new', eventProps);
    }
}

export function createThemeOption (theme, showActions = true) {
    const themeId = theme.name;
    const totalResponsesForTheme = theme.promoters + theme.passives + theme.detractors || 0;
    const isSpecialMLTheme = Object.values(ML_SPECIAL_NPS_THEME_IDS).includes(themeId);
    const themeLabel = getThemeDisplayName(theme);

    return {
        id: themeId,
        name: theme.name,
        count: totalResponsesForTheme,
        actions: !showActions || isSpecialMLTheme || themeId === noThemeResponses.value ? [] : npsThemesDropdownActions,
        themeLabel,
        label: `${themeLabel} (${totalResponsesForTheme})`,
    };
}

export function sortedThemeOptions (themes, showActions = true) {
    const npsThemesList = themes.map((theme) => {
        return createThemeOption(theme, showActions);
    });

    const sortedNpsThemesList = npsThemesList
        .sort((firstValue, secondValue) => (firstValue.count > secondValue.count ? -1 : 1))
        .sort((firstValue, secondValue) =>
            (firstValue.themeLabel.toLowerCase() > secondValue.themeLabel.toLowerCase() ? 1 : -1),
        )
        .sort(moveToLastEntry(likesDislikesTheme, 'id'))
        .sort(moveToLastEntry(generalNpsTheme, 'id'));

    return sortedNpsThemesList;
}

export function mapResponseThemes (response) {
    const { selectedNpsThemes, suggestedNpsThemes } = response;

    return {
        selectedNpsThemes: selectedNpsThemes
            .map((theme) => {
                return {
                    id: theme.name,
                    label: getThemeDisplayName(theme),
                    isSuggested: suggestedNpsThemes.some((suggestedTheme) => suggestedTheme.name === theme.name),
                };
            })
            .sort((firstValue, secondValue) => {
                if (firstValue.isSuggested === secondValue.isSuggested) return 0;
                if (firstValue.isSuggested) return -1;

                return 1;
            }),
        suggestedNpsThemes: suggestedNpsThemes.map((theme) => ({
            id: theme.name,
            label: getThemeDisplayName(theme),
        })),
    };
}
